<template>
  <div class="form-container card shadow-sm p-3">
    <!-- Cabeçalho -->
    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ isEditing ? "Editar Formulário de Avaliação nº" : "Novo Formulário de Avaliação" }}
        <span v-if="isEditing">{{ idFormulario }}</span>
      </h4>
      <button class="btn btn-secondary" @click="cancelar">Voltar</button>
    </div>

    <div class="spacer"></div>

    <!-- Formulário -->
    <b-form @submit.prevent="submitFormulario">
      <!-- Seção: Dados do Formulário -->
      <div class="form-section">
        <div class="form-group">
          <label for="formulario">
            Nome do Formulário <span class="required">*</span>
          </label>
          <input
            type="text"
            id="formulario"
            class="form-control"
            v-model="formulario"
            placeholder="Informe o nome do formulário"
            required
          />
        </div>
      </div>

      <!-- Seção: Filtro por Categoria -->
      <div class="form-section">
        <div class="row g-2 align-items-end">
          <div class="col-md-6">
            <label for="categoria">Categoria<span class="required">*</span></label>
            <select
              id="categoria"
              class="form-control"
              v-model="selectedCategoriaId"
            >
              <option :value="null" disabled>Selecione uma categoria</option>
              <option
                v-for="cat in categorias"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.categoria }}
              </option>
            </select>
          </div>
          <div class="col-md-6">
            <small class="text-muted">
              Escolha uma categoria para limitar as questões disponíveis.
            </small>
          </div>
        </div>
      </div>

      <!-- Seção: Questões Selecionadas (usando tabela borderless) -->
      <div class="form-section">
        <h5 class="section-title">Questões Selecionadas</h5>
        <!-- 
          Adicione align-middle diretamente na tabela ou nas <tr>.
          .align-middle => cada célula <td> terá vertical-align: middle;
        -->
        <table class="table table-borderless align-middle mb-0">
          <tbody>
            <tr v-for="(questao, index) in questoesSelecionadas" :key="index">
              <td class="w-100">
                <select
                  v-model="questoesSelecionadas[index].id"
                  @change="atualizarQuestao(index)"
                  class="form-control"
                >
                  <option value="" disabled>Selecione uma questão</option>
                  <option
                    v-for="opcao in getOpcoesParaLinha(index)"
                    :key="opcao.value"
                    :value="opcao.value"
                    :disabled="isOptionDisabled(opcao.value, index)"
                  >
                    {{ opcao.text }}
                  </option>
                </select>
              </td>
              <td class="text-end" style="width: 1%;">
                <button
                  type="button"
                  class="btn btn-danger"
                  @click="removerQuestao(index)"
                  title="Remover questão"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="questoesSelecionadas.length === 0">
              <td class="text-center" colspan="2">Nenhuma questão adicionada.</td>
            </tr>
          </tbody>
        </table>

        <!-- Botão de adicionar questão -->
        <div class="add-questao-container mt-3">
          <button
            type="button"
            class="btn btn-primary"
            @click="adicionarQuestaoSlot"
            :disabled="!selectedCategoriaId"
            title="Adicionar questão"
          >
            <i class="bi bi-plus"></i>
          </button>
        </div>

      </div>

      <!-- Botões de Ação -->
      <div class="form-actions d-flex justify-content-end">
        <button
          type="submit"
          :class="['btn', isSubmitting ? 'btn-success' : 'btn-primary', 'me-2']"
          :disabled="!formValid || isSubmitting"
        >
          <i :class="isSubmitting ? 'bi bi-hourglass-split me-1' : 'bi bi-save me-1'"></i>
          {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          @click="cancelar"
          :disabled="isSubmitting"
        >
          <i class="bi bi-x-circle me-1"></i>Cancelar
        </button>
      </div>
    </b-form>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { BForm } from "bootstrap-vue-next";
import axios from "@/services/axios";

export default {
  name: "FormularioFormularioAvaliacao",
  components: { BForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const formulario = ref("");
    const questoesSelecionadas = ref([]);

    // NOVO: categorias e categoria selecionada
    const categorias = ref([]);
    const selectedCategoriaId = ref(null);

    // Guardaremos TODAS as questões em memória (com id_categoria)
    const todasOpcoesQuestao = ref([]);
    // Mantemos também um Map id->texto para lookup rápido
    const textoPorQuestaoId = ref(new Map());

    const isEditing = ref(false);
    const idFormulario = ref(null);
    const isSubmitting = ref(false);

    const formValid = computed(() => formulario.value.trim() !== "");

    const isOptionDisabled = (value, rowIndex) => {
      if (!value) return false;
      return questoesSelecionadas.value.some((q, idx) => idx !== rowIndex && q.id === value);
    };


    // ---- Carregadores ----
    const carregarCategorias = async () => {
      try {
        // ajuste a rota conforme seu backend (ver seção 2)
        const { data } = await axios.get("/configuracoes/parametros-avaliacoes/categorias");
        categorias.value = (data?.data || data || []).map(c => ({
          id: c.id,
          categoria: c.categoria
        })).sort((a,b)=> a.categoria.localeCompare(b.categoria));
      } catch (err) {
        console.error("Erro ao carregar categorias:", err.message);
      }
    };

    const carregarOpcoesQuestao = async (idCategoria = null) => {
      try {
        // Preferimos filtrar no servidor para listas muito grandes
        const params = {};
        if (idCategoria) params.id_categoria = idCategoria; // novo filtro exato

        const response = await axios.get(
          "/configuracoes/parametros-avaliacoes/questoes/todos",
          { params }
        );

        // IMPORTANTE: backend agora devolve q.id_categoria (ver seção 2)
        const rows = response.data.data || response.data;
        // Mapeia e mantém id_categoria
        const mapped = rows.map(q => ({
          value: q.id,
          text: `${q.bloco_nome} / ${q.questao}`,
          id_categoria: q.id_categoria
        }));

        if (idCategoria) {
          // Se filtramos no servidor, atualizamos apenas subconjunto visível,
          // mas também atualizamos textoPorQuestaoId (segurança)
          mapped.forEach(m => textoPorQuestaoId.value.set(m.value, m.text));
        } else {
          // Se carregarmos tudo, persistimos global
          textoPorQuestaoId.value = new Map(mapped.map(m => [m.value, m.text]));
        }

        // Estratégia: sempre manter "todasOpcoesQuestao" atualizado
        // Quando há idCategoria, podemos mesclar mantendo únicas por id
        if (!idCategoria) {
          todasOpcoesQuestao.value = mapped;
        } else {
          const byId = new Map(todasOpcoesQuestao.value.map(m => [m.value, m]));
          mapped.forEach(m => byId.set(m.value, m));
          todasOpcoesQuestao.value = Array.from(byId.values());
        }

      } catch (error) {
        console.error("Erro ao carregar questões:", error.message);
      }
    };

    // ---- Funções de UI ----
    const adicionarQuestaoSlot = () => {
      questoesSelecionadas.value.push({
        id: "",
        textoConcatenado: ""
      });
    };

    const atualizarQuestao = (index) => {
      const questaoId = questoesSelecionadas.value[index].id;

      // bloqueia duplicata
      const jaUsada = questoesSelecionadas.value.some((q, i) => i !== index && q.id === questaoId);
      if (jaUsada) {
        // volta ao estado vazio (ou mantenha o antigo se você armazenar)
        questoesSelecionadas.value[index].id = "";
        // opcional: toast/alert
        console.warn("Esta questão já foi selecionada.");
        return;
      }

      const texto = textoPorQuestaoId.value.get(questaoId);
      if (texto) questoesSelecionadas.value[index].textoConcatenado = texto;
    };

    const removerQuestao = (index) => {
      questoesSelecionadas.value.splice(index, 1);
    };

    // Retorna as opções filtradas pela categoria escolhida,
    // MAS garante incluir a opção atualmente selecionada (se existir)
    const getOpcoesParaLinha = (index) => {
      const selecionadaId = questoesSelecionadas.value[index]?.id || null;

      let base = todasOpcoesQuestao.value;
      if (selectedCategoriaId.value) {
        base = base.filter(op => op.id_categoria === selectedCategoriaId.value);
      }

      // se a questão atualmente selecionada não estiver no "base", inclui-a
      if (selecionadaId) {
        const jaTem = base.some(op => op.value === selecionadaId);
        if (!jaTem) {
          const texto = textoPorQuestaoId.value.get(selecionadaId) || "Questão selecionada (fora do filtro)";
          base = [
            ...base,
            { value: selecionadaId, text: texto, id_categoria: null }
          ];
        }
      }

      // ordena por texto
      return base.slice().sort((a,b)=> a.text.localeCompare(b.text));
    };

    const cancelar = () => router.push("/configuracoes/formularios-avaliacoes");

    const submitFormulario = async () => {
      if (isSubmitting.value || !formValid.value) return;

      // valida duplicatas
      const ids = questoesSelecionadas.value.map(q => q.id).filter(Boolean);
      const temDuplicata = new Set(ids).size !== ids.length;
      if (temDuplicata) {
        console.error("Há questões repetidas. Remova as duplicadas antes de salvar.");
        return; // opcional: exibir toast
      }

      isSubmitting.value = true;
      const payload = {
        formulario: formulario.value,
        questoes: ids,
      };
      try {
        if (isEditing.value) {
          await axios.put(`/configuracoes/formularios-avaliacoes/${idFormulario.value}`, payload);
        } else {
          await axios.post(`/configuracoes/formularios-avaliacoes`, payload);
        }
        router.push("/configuracoes/formularios-avaliacoes");
      } catch (error) {
        console.error("Erro ao salvar formulário:", error.message);
      } finally {
        isSubmitting.value = false;
      }
    };

    const carregarFormularioParaEdicao = async (id) => {
      try {
        const { data } = await axios.get(`/configuracoes/formularios-avaliacoes/${id}`);

        idFormulario.value = data.id;
        formulario.value = data.formulario;

        // Preenche as questões selecionadas e o mapa de textos
        if (Array.isArray(data.questoes)) {
          questoesSelecionadas.value = data.questoes.map(q => {
            const texto = `${q.bloco} / ${q.questao}`; // sem categoria no rótulo
            textoPorQuestaoId.value.set(q.id, texto);
            return { id: q.id, textoConcatenado: texto, id_categoria: q.id_categoria };
          });

          // Inferir categoria única
          const setCats = new Set(
            data.questoes.map(q => Number(q.id_categoria)).filter(v => !Number.isNaN(v))
          );

          if (setCats.size === 1) {
            selectedCategoriaId.value = [...setCats][0];
            await carregarCategorias();                 // garantir que o select tenha as opções
            await carregarOpcoesQuestao(selectedCategoriaId.value); // já carrega filtrado
          } else {
            selectedCategoriaId.value = null;           // múltiplas categorias (ou nenhuma)
            await carregarCategorias();
            await carregarOpcoesQuestao(null);          // carrega (ou mantém) sem filtro
          }
        }
      } catch (error) {
        console.error("Erro ao carregar formulário para edição:", error.message);
      }
    };

    // Quando escolher categoria, recarrega questões filtradas do servidor
    watch(selectedCategoriaId, async (val) => {
      if (val) await carregarOpcoesQuestao(val);
    });

    onMounted(async () => {
      await carregarCategorias();
      // carregue inicial (opcional: todas, ou nenhum até escolher categoria)
      await carregarOpcoesQuestao(null);

      if (route.params.id) {
        isEditing.value = true;
        await carregarFormularioParaEdicao(route.params.id);
      }
    });

    return {
      // state
      formulario,
      questoesSelecionadas,
      categorias,
      selectedCategoriaId,

      // methods
      getOpcoesParaLinha,
      adicionarQuestaoSlot,
      atualizarQuestao,
      removerQuestao,
      submitFormulario,
      cancelar,

      // flags
      isSubmitting,
      formValid,
      isEditing,
      idFormulario,
      isOptionDisabled,
    };
  },
};
</script>

<style scoped>
.form-container {
  margin: 20px;
  width: 90%;
}
.header {
  margin-bottom: 10px;
}
.form-title {
  font-size: 1.25rem;
  font-weight: bold;
}
.spacer {
  margin-bottom: 20px;
}
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}
.form-actions {
  margin-top: 20px;
}
.required {
  color: red;
}
.add-questao-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
}
.btn-danger {
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
}
  

</style>
